// OpenAI TTS Utility
// Handles text-to-speech using OpenAI's TTS API

import { trackTTSAPI } from './apiTracker';
import { getApiBaseUrl } from './apiBaseUrl';

class OpenAITTS {
  constructor() {
    this.currentAudio = null;
    this.isPlaying = false;
    this.isPaused = false;
    this.currentText = '';
    this.callbacks = {};
    this.debugListeners = [];
    this.debugEnabled = false;
  }

  // Debug logging system
  enableDebug() {
    this.debugEnabled = true;
    this.log('debug', 'TTS Debug logging enabled');
  }

  disableDebug() {
    this.debugEnabled = false;
  }

  addDebugListener(callback) {
    this.debugListeners.push(callback);
  }

  removeDebugListener(callback) {
    this.debugListeners = this.debugListeners.filter(listener => listener !== callback);
  }

  log(level, message, data = null) {
    if (!this.debugEnabled) return;
    
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
      source: 'OpenAITTS'
    };

    console.log(`[TTS Debug] ${level.toUpperCase()}:`, message, data || '');
    
    // Notify debug listeners
    this.debugListeners.forEach(listener => {
      try {
        listener(logEntry);
      } catch (error) {
        console.error('Debug listener error:', error);
      }
    });
  }

  async speak(text, options = {}) {
    const {
      voice = 'echo',
      model = 'tts-1',
      onStart = () => {},
      onEnd = () => {},
      onError = () => {},
      onPause = () => {},
      onResume = () => {}
    } = options;

    // Validate text input
    if (!text || typeof text !== 'string' || !text.trim()) {
      const error = new Error('Invalid text input for TTS');
      this.log('error', 'Invalid text input', { text, textType: typeof text });
      onError(error);
      return;
    }

    this.log('info', 'Starting TTS speak request', { 
      textLength: text.length, 
      voice, 
      model,
      textPreview: text.substring(0, 100) + (text.length > 100 ? '...' : '')
    });

    // Stop any existing audio
    this.stop();

    this.currentText = text;
    this.callbacks = { onStart, onEnd, onError, onPause, onResume };

    try {
      const apiBaseUrl = getApiBaseUrl();
      const ttsUrl = `${apiBaseUrl}/api/tts`;
      
      this.log('info', 'Making TTS API request', { 
        url: ttsUrl,
        apiBaseUrl,
        method: 'POST',
        voice,
        model
      });

      // Call the TTS API
      const response = await trackTTSAPI(ttsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: text,
          voice: voice,
          model: model
        }),
        credentials: 'omit'
      });

      this.log('info', 'TTS API response received', { 
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type'),
        contentLength: response.headers.get('content-length')
      });

      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
          // Try to parse as JSON for better error messages
          try {
            const errorJson = JSON.parse(errorText);
            if (errorJson.error) {
              errorText = errorJson.error;
            }
          } catch (e) {
            // Not JSON, use text as is
          }
        } catch (e) {
          errorText = `HTTP ${response.status}`;
        }
        
        this.log('error', 'TTS API error response', { 
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        
        // Provide user-friendly error messages
        if (response.status === 503) {
          throw new Error('TTS service is not configured. Please check server configuration.');
        } else if (response.status === 400) {
          throw new Error(`Invalid request: ${errorText || 'Please check your input'}`);
        } else if (response.status === 500) {
          throw new Error(`Server error: ${errorText || 'Please try again later'}`);
        } else {
          throw new Error(`TTS error (${response.status}): ${errorText || 'Please check your connection and try again'}`);
        }
      }

      // Create audio from response
      this.log('info', 'Processing audio blob');
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      this.log('info', 'Audio blob created', { 
        blobSize: audioBlob.size,
        blobType: audioBlob.type,
        audioUrl: audioUrl.substring(0, 50) + '...'
      });

      this.currentAudio = new Audio(audioUrl);
      
      // Set audio properties to help with autoplay policy
      this.currentAudio.preload = 'auto';
      this.currentAudio.muted = false;
      this.currentAudio.volume = 1.0;

      // Set up event listeners
      this.currentAudio.onplay = () => {
        this.log('info', 'Audio playback started');
        this.isPlaying = true;
        this.isPaused = false;
        this.callbacks.onStart();
      };

      this.currentAudio.onended = () => {
        this.log('info', 'Audio playback ended');
        this.isPlaying = false;
        this.isPaused = false;
        this.callbacks.onEnd();
        // Clean up
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
      };

      this.currentAudio.onerror = (error) => {
        this.log('error', 'Audio playback error', { 
          error: error.message,
          errorCode: error.code,
          errorType: error.type
        });
        this.isPlaying = false;
        this.isPaused = false;
        this.callbacks.onError(error);
        // Clean up
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
      };

      this.currentAudio.onloadstart = () => {
        this.log('info', 'Audio loading started');
      };

      this.currentAudio.oncanplay = () => {
        this.log('info', 'Audio can start playing');
      };

      this.currentAudio.onwaiting = () => {
        this.log('info', 'Audio is waiting for data');
      };

      this.currentAudio.onstalled = () => {
        this.log('warn', 'Audio loading stalled');
      };

      // Start playing
      this.log('info', 'Attempting to start audio playback');
      
      // Browser autoplay policy requires user interaction
      // Try to play, and if it fails, provide helpful error message
      try {
        const playPromise = this.currentAudio.play();
        
        if (playPromise !== undefined) {
          await playPromise;
        }
        
        this.log('success', 'Audio playback started successfully');
      } catch (playError) {
        // Handle autoplay policy error
        if (playError.name === 'NotAllowedError' || playError.message.includes('not allowed')) {
          this.log('error', 'Audio playback blocked by browser autoplay policy', {
            error: playError.message,
            suggestion: 'User interaction required to play audio'
          });
          
          // Try to enable autoplay by setting audio properties
          this.currentAudio.muted = false;
          this.currentAudio.volume = 1.0;
          
          // Create a user-friendly error message
          const error = new Error('Audio playback requires user interaction. Please click the play button or interact with the page first.');
          error.name = 'NotAllowedError';
          throw error;
        } else {
          // Re-throw other errors
          throw playError;
        }
      }

    } catch (error) {
      this.log('error', 'TTS speak error', { 
        error: error.message,
        stack: error.stack,
        name: error.name
      });
      console.error('OpenAI TTS Error:', error);
      this.callbacks.onError(error);
    }
  }

  pause() {
    if (this.currentAudio && this.isPlaying && !this.isPaused) {
      this.log('info', 'Pausing audio playback');
      this.currentAudio.pause();
      this.isPaused = true;
      this.isPlaying = false;
      this.callbacks.onPause();
    } else {
      this.log('warn', 'Cannot pause audio - not currently playing', {
        hasAudio: !!this.currentAudio,
        isPlaying: this.isPlaying,
        isPaused: this.isPaused
      });
    }
  }

  resume() {
    if (this.currentAudio && this.isPaused) {
      this.log('info', 'Resuming audio playback');
      
      try {
        const playPromise = this.currentAudio.play();
        
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            if (error.name === 'NotAllowedError' || error.message.includes('not allowed')) {
              this.log('error', 'Audio resume blocked by browser autoplay policy', {
                error: error.message
              });
              // Don't throw, just log - resume is usually called after user interaction
            } else {
              this.log('error', 'Audio resume error', { error: error.message });
            }
          });
        }
        
        this.isPaused = false;
        this.isPlaying = true;
        this.callbacks.onResume();
      } catch (error) {
        this.log('error', 'Audio resume failed', { error: error.message });
        // Don't throw - resume is usually called after user interaction
      }
    } else {
      this.log('warn', 'Cannot resume audio - not currently paused', {
        hasAudio: !!this.currentAudio,
        isPlaying: this.isPlaying,
        isPaused: this.isPaused
      });
    }
  }

  stop() {
    if (this.currentAudio) {
      this.log('info', 'Stopping audio playback');
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.isPlaying = false;
      this.isPaused = false;
      
      // Don't call onEnd callback for manual stop
      // onEnd should only be called when audio ends naturally
      this.log('info', 'Manual stop - not calling onEnd callback');
      
      this.currentAudio = null;
    } else {
      this.log('info', 'Stop called but no audio is currently playing');
    }
  }

  isCurrentlyPlaying() {
    return this.isPlaying;
  }

  isCurrentlyPaused() {
    return this.isPaused;
  }
}

// Export singleton instance
const openaiTTS = new OpenAITTS();
export default openaiTTS;
